import { HardwareItem, HwOpening} from "../../../../api/Hardware";
import {colCount, indentWidth} from "./HardwareTable";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Button, Chip, Divider, Grid, IconButton, List, MenuItem} from "@mui/material";
import {IconButton2, IconCell} from "./library/IconCell";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {css} from "@emotion/css";
import {buildFilterLink} from "../../../../misc/scroller/Filter";
import {useProject, useProjectId} from "../ProjectName";
import {openingsTableName} from "../openings/Openings";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {useReloader} from "./ReloadContext";
import {ProductSearchResult} from "../../../../api/Products";
import {ShortCode} from "../../../../api/Library";
import {ProductLookup} from "../extras/ProductLookup";
import {getHwOpeningType, isShortCode, toProductQty} from "./PendingHardware";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {OpeningLookup} from "./OpeningLookup";
import {RenameHardwareGroup} from "./RenameHardwareGroup";
import {EditCell} from "./library/EditCell";
import {HWFilter, qtyWidth} from "./WorkingHardware";
import {EditProductContext} from "./EditProduct";
import {ProductDimensioned} from "./library/ProductDimensioned";
import {useHwAPI} from "./HardwareGeneric";
import {ChangesetContext} from "../changeset/ChangesetContext";
import {cellInActive} from "../pricing/cellconst";
import {AutoForHingeCell} from "./library/AutoForHingeCell";
import {StatusCell} from "./StatusCell";
import {
    EditHardwareGroupWithContent,
    EditHardwareItem,
    HwEditingContext,
    makeHeadingId
} from "./HwEditingContextProvider";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {Opening} from "../../../../api/Openings";
import {distinctByKey, first} from "nate-react-api-helpers";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {ResolveShortcodeChangesDialog} from "./ResolveShortcodeChangesDialog";
import {ConfirmShortcodeChange, HardwareGroupUpsertResponse, PrepMigration} from "../../../../api/QuoteHardware";
import {PopoutMenu} from "./library/PopoutMenu";
import {grey, red} from "@mui/material/colors";
import {ConfirmContext} from "../../../../misc/Confirm";
import {matchesPhaseFilter, phaseLabel} from "../../../../misc/Phase";
import {useFocusMode} from "./UseFocusMode";
import {HardwareItemMenu} from "./HardwareItemMenu";
import {PrepManager} from "./prepCanvas/PrepManager";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {HardwareViewModeContext} from "../../quote/hardware/HardwareViewMode";
import {decodeHanding, hasCustomPrep} from "../../quote/openings/QuoteOpenings";
import {InfoPopout} from "./InfoPopout";
import {HwUpdateCustomPreps} from "./HwUpdatePreps";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";
import {BulkUpdateOpenings} from "./bulkUpdateOpenings/BulkUpdateOpenings";
import {BulkUpdateSuppliedBy} from "./BulkUpdateSuppliedBy";
import {api} from "../../../../api/API";
import Warning from "@mui/icons-material/Warning";
import {useMultiHingeConfig} from "./MultiHingeConfig";
import {ChatBubbleOutline, MarkChatUnread} from "@mui/icons-material";
import {EditNotesDialog} from "./EditNotesDialog";
import {RowNumberIndicator} from "./RowNumberIndicator";
import {ConfigureHardwareItemExt} from "./ConfigureHardwareItemExt";
import {DegreesOpeningDetail} from "./DegreesOpeningDetail";
import {MyRefObject, useBackgroundAction} from "../../manage/sov/UseBackgroundTask";
import {makeNumberOrUndefined} from "../../quote/openings/util";
import {ItemCalcErrors} from "./ItemCalcErrors";
import {LogiCtrlOpeningCell} from "../openings/logictrl/OpeningCell";
import {LogiCtrlHwGroup} from "../openings/logictrl/LogiCtrlHwGroup";
import { SupplierChip } from "./SupplierChip";
import {GroupTimelineView} from "./timeline/GroupTimelineView";
import {useSnackAction} from "./SnackAction";
import {HwGroupElevationLink} from "./HwGroupElevationLink";

export const actionColWidth = 30;

export const HardwareGroup = React.memo(function HardwareGroup(props: {
    filter: HWFilter;
    hasFilter: boolean;
    value: EditHardwareGroupWithContent;
    isCreate?: boolean;
    createNeedsLocationConfirmation?: boolean;
    onCreateNeedsLocationConfirmation(value: number | undefined): void
}) {
    const [headerRowRef, setHeaderRowRef] = useState<HTMLTableRowElement | null>(null);
    const [value, setValue] = useState(props.value);
    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    const [lastEditedTimestamp, setLastEditedTimestamp] = useState(new Date());
    const [lastActionWasSearch, setLastActionWasSearch] = useState<boolean>();

    const editCtx = useContext(HwEditingContext);
    const editCtxRef = useSyncedRef(editCtx);
    // compare with props.value because our draft may edit "value"
    const isEditing = !!(editCtx.creating && editCtx.creating === props.value) || !!(editCtx.editing && editCtx.editing === props.value);
    const propsValueRef = useSyncedRef(props.value);

    const edit = useCallback((value: EditHardwareGroupWithContent, source?: "search") => {
        if(!editCtxRef.current.editing && !editCtxRef.current.creating) {
            editCtxRef.current.onEdit(propsValueRef.current);
        }

        setValue(value);
        setLastEditedTimestamp(new Date());
        setLastActionWasSearch(source === "search");
    }, [editCtxRef, propsValueRef]);
    
    const [collapseHeader, setCollapseHeader] = useState(false);

    const project = useProjectId();
    const reloadRef = useReloader();
    const hapi = useHwAPI();

    const [showRenameDialog, setShowRenameDialog] = useState(false);

    const hasOpeningFilter = !!props.filter.opening || props.filter.phases.length > 0;
    const filteredOpenings = hasOpeningFilter ? value.openings.filter(o => {
        if(props.filter.opening && o.descriptiveName?.toLowerCase().indexOf(props.filter.opening.toLowerCase()) !== 0) return false;
        return matchesPhaseFilter(o.phase, props.filter.phases);
    }) : value.openings;

    const shouldCollapse = value.openings.length > 5 && !isEditing;
    useEffect(() => {
        setCollapseHeader(shouldCollapse && !hasOpeningFilter);
    }, [shouldCollapse, hasOpeningFilter]);

    const productEdit = useContext(EditProductContext);

    const hardwareFilter = props.hasFilter ? value.hardware.filter(h => {
        if(props.filter.code && h.productCode?.toLowerCase().indexOf(props.filter.code.toLowerCase()) !== 0) return false;
        if(props.filter.finish && h.finish?.toLowerCase().indexOf(props.filter.finish.toLowerCase()) !== 0) return false;
        if(props.filter.dimensions && h.dimensions?.toLowerCase().indexOf(props.filter.dimensions.toLowerCase()) !== 0) return false;
        if(props.filter.note && h.note?.toLowerCase().indexOf(props.filter.note.toLowerCase()) !== 0) return false;

        if(!h.productIsAutoQtyHinge) { // hinges don't have defined quantities
            if (props.filter.activeQty && h.activeQty?.toString() !== props.filter.activeQty) return false;
            if (props.filter.inactiveQty && h.inactiveQty?.toString() !== props.filter.inactiveQty) return false;
            if (props.filter.commonQty && h.commonQty?.toString() !== props.filter.commonQty) return false;
        }

        return true;
    }) : value.hardware;

    const changeset = useContext(ChangesetContext);
    const greyed = changeset.proposing && (!value.backup || value.name === value.backup?.name);

    useFocusMode({
        enabled: isEditing,
        focusRef: headerRowRef,
        allowScroll: true,
        adjustFocus: (row: HTMLElement, retryLater): Element[] => {
            const table = row.parentElement;
            if(!table) {
                retryLater();
                return [];
            }

            const rows = Array.from(table.children);
            let hasStart = false;
            let filteredRows: Element[] = [];

            for(let i = 0; i < rows.length; i++) {
                if(rows[i] === headerRowRef) {
                    hasStart = true;
                    filteredRows.push(rows[i]);
                    continue
                }

                if(!hasStart) continue

                // @ts-ignore
                if(rows[i].dataset.headerGroup) {
                    hasStart = false;
                    continue
                }

                filteredRows.push(rows[i]);
            }

            if(filteredRows.length === 0) {
                console.log("no matching rows")
                return [];
            }

            return filteredRows;
        }
    });
    
    const editOnDoneRef = useSyncedRef(editCtx.onDone);
    const [showShortcodeDetail, setShowShortcodeDetail] = useState<ConfirmShortcodeChange|null>(null);
    const lastUpdateCallbackValue = useRef<EditHardwareGroupWithContent|null>(null);
    const [showCustomPreps, setShowCustomPreps] = useState(props.createNeedsLocationConfirmation || false);
    const onCreateNeedsLocationConfirmationRef = useSyncedRef(props.onCreateNeedsLocationConfirmation);

    const multiHingeDontRePromptRef = useRef(false);

    const multiHinge = useMultiHingeConfig();

    // a drw hardware elevation owns its opening's hinge/prep layout. The legacy Multi-Hinge and
    // Customize Hardware Preps options stay while any opening is legacy (they note the drw openings),
    // and the automatic prompts are skipped when any opening is drw.
    const openings = value.openings || [];
    const anyDrwHardware = openings.some(o => o.drwHardwareElevation != null);
    const allDrwHardware = openings.length > 0 && openings.every(o => o.drwHardwareElevation != null);
    const anyDrwHardwareRef = useSyncedRef(anyDrwHardware);

    type UpdateInput = {
        value: EditHardwareGroupWithContent
        createShortcode?: {
            name: string;
            description: string;
        };
        updateShortcode?: number;
        linkToShortcode?: number;
        prepMigrationResolution?: PrepMigration[]
        confirmElevationUpdates?: boolean;
    };

    type UpdateRef = {
        input: UpdateInput;
        value: EditHardwareGroupWithContent;
        becameOverride: boolean;
        isCreateWithOverride: boolean;
    }
    
    const update = useBackgroundAction<UpdateInput, UpdateRef>({
        callback: async (input: UpdateInput, sharedRef: MyRefObject<UpdateRef>) => {
            // check if auto-qty changed
            const oldValue = propsValueRef.current;
            let newValue = input.value;

            if (!anyDrwHardwareRef.current && multiHinge.shouldPrompt(newValue, oldValue)) {
                try {
                    newValue = await multiHinge.prompt(input.value);
                    input.value = newValue;
                    setValue(newValue);
                    multiHingeDontRePromptRef.current = true;
                } finally {
                    setShowShortcodeDetail(null)
                    if (!props.isCreate) {
                        editOnDoneRef.current();
                        reloadRef.current();
                    }
                }
            }  else if(multiHinge.has(newValue)) {
                // do nothing
            } else {
                // clear any prep locations
                newValue.hardware.forEach(h => {
                    h.hingePrepLocationConfig = null;
                })
            }

            const becameOverride = oldValue.hardware.some(h => {
                const newH = newValue.hardware.find(nh => nh.product === h.product);
                if(!newH) return false;

                if(h.overrideHingeActiveQty !== newH.overrideHingeActiveQty) return true;
                if(h.overrideHingeInActiveQty !== newH.overrideHingeInActiveQty) return true;
                return false;
            });

            const isCreateWithOverride = newValue.hardware.some(h => {
                const old = oldValue.hardware.find(nh => nh.product === h.product);
                if(old) return false;

                return h.overrideHingeActiveQty || h.overrideHingeInActiveQty
            });

            sharedRef.current = {
                input,
                becameOverride,
                isCreateWithOverride,
                value: input.value,
            }

            return hapi.upsertTask(input);
        },
        onDone: (resp: HardwareGroupUpsertResponse, sharedRef) => {
            lastUpdateCallbackValue.current = sharedRef.current?.value || null;

            if(resp.updated) {
                setShowShortcodeDetail(null)
                editOnDoneRef.current();
                reloadRef.current();

                if(!anyDrwHardwareRef.current && (sharedRef.current?.becameOverride || sharedRef.current?.isCreateWithOverride)) {
                    setShowCustomPreps(true);

                    if(props.isCreate) {
                        onCreateNeedsLocationConfirmationRef.current(resp.id);
                    }
                }

                return;
            }

            // the hinge change reaches linked hardware elevations: ask, then save again with the go-ahead.
            const elevations = resp.elevationUpdates || [];
            if(elevations.length > 0) {
                const input = sharedRef.current?.input;
                confirm.confirm(`This hinge change will auto-update hardware elevations ${elevations.join(", ")}. Continue?`).then(ok => {
                    if(ok && input) update.callback({...input, confirmElevationUpdates: true});
                });
                return;
            }

            setShowShortcodeDetail(resp.confirmShortcodeChanges)
        }
    });

    const confirm = useContext(ConfirmContext);
    const [showHardwarePreps, setShowHardwarePreps] = useState(false);
    const [showBulkUpdateOpenings, setShowBulkUpdateOpenings] = useState(false);
    const [showBulkUpdateSuppliedBy, setShowBulkUpdateSuppliedBy] = useState(false);
    const previewChangeset = useContext(ChangePreviewContext);
    const viewMode = useContext(HardwareViewModeContext).viewMode;
    const proj = useProject();
    const isQuoteMode = useIsQuote();
    const alt = useAlternative();

    const lockedQuoteMode = isQuoteMode && proj?.quoteLockedHardware === alt
    const locked = previewChangeset.enabled || viewMode !== "default" || lockedQuoteMode;

    const recalculate = useSnackAction(async (input: {openings: number[]}) => {
        await api.openings.recalculate({project: project, openings: input.openings});
        reloadRef.current();
    }, {
        success: "Recalculated",
    });

    const info = useMemo(() => {
        if(value.openings.length === 0) return null;
        const op = value.openings[0]
        const handing = decodeHanding(op.handing || "", value.flipDoubleActiveHanding);

        return {
            activeWidth: op.nominalWidth,
            inActiveWidth: op.inactiveDoorWidth,
            activeHanding: handing.active,
            inActiveHanding: handing.inActive,
            bothActive: handing.doubleActive,
        }
    }, [value]);

    const [showNotes, setShowNotes] = useState(false);
    const [showTimeline, setShowTimeline] = useState(false);

    if(hasOpeningFilter && filteredOpenings.length === 0) return null;

    return (
        <>
            {multiHinge.Element}

            {showCustomPreps && <HwUpdateCustomPreps value={props.value} onDone={() => {
                setShowCustomPreps(false)
                if(props.createNeedsLocationConfirmation) {
                    onCreateNeedsLocationConfirmationRef.current(undefined);
                }
            }} />}

            {showBulkUpdateOpenings && <BulkUpdateOpenings
                project={props.value.project}
                alternative={props.value.quoteAlternative || 0}
                hwGroup={props.value.id}
                onClose={() => setShowBulkUpdateOpenings(false)}
                onUpdated={() => {
                    setShowBulkUpdateOpenings(false);
                    reloadRef.current();
                }}
            />}

            {showBulkUpdateSuppliedBy && <BulkUpdateSuppliedBy
                name={value.name || ""}
                onClose={() => setShowBulkUpdateSuppliedBy(false)}
                onSelect={(supplier) => {
                    setShowBulkUpdateSuppliedBy(false);
                    update.callback({
                        value: Object.assign({}, value, {
                            hardware: value.hardware.map(h => Object.assign({}, h, {
                                supplierCategory: supplier.id,
                                supplierCategoryName: supplier.name,
                            })),
                        }),
                    });
                }}
            />}

            {showNotes && <EditNotesDialog
                value={props.value}
                onClose={() => setShowNotes(false)}
                onUpdated={() => {
                    setShowNotes(false);
                    reloadRef.current();
                }}
            />}

            {showTimeline && <GroupTimelineView
                group={props.value as any}
                onClose={() => setShowTimeline(false)}
            />}

            <tr key="group-name" data-header-group="true" ref={setHeaderRowRef}>
                <td style={{
                    width: actionColWidth,
                    backgroundColor: greyed ? cellInActive : undefined,
                }}></td>
                <td style={{
                    fontWeight: "500",
                    backgroundColor: greyed ? cellInActive : undefined,
                }} colSpan={colCount - 1}>
                    {showShortcodeDetail && <ResolveShortcodeChangesDialog
                        value={showShortcodeDetail}
                        error={update.error}
                        errorDetail={update.error && update.raw ? update.raw.details : null}
                        onCancel={() => setShowShortcodeDetail(null)}
                        loading={update.loading}
                        onDone={(input) => {
                            update.callback({
                                value: lastUpdateCallbackValue.current || value, // use last callback, b/c we may call update for a duplicate action which uses a different value than the "state" value.
                                createShortcode: input.createShortcode,
                                updateShortcode: input.updateShortcode,
                                linkToShortcode: makeNumberOrUndefined(input.linkToShortcode),
                                prepMigrationResolution: input.prepMigrationResolution,
                            })
                        }}/>}
                    <div style={{display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 8}}>
                        <div id={makeHeadingId(value.id)}>
                            Heading {value.name || ""}{value.prepConfigIsCustom ? " (Custom Locations)" : null}
                        </div>
                        {!isQuoteMode && <div>
                            <LogiCtrlHwGroup name={value.name || ""} openings={value.openings} />
                        </div>}
                        <div>
                            <IconButton color={value.notes ? "primary" : "default"} size="small" style={{height: 16}} onClick={() => {
                                setShowNotes(true);
                            }}>
                                {value.notes ? <MarkChatUnread fontSize="inherit" /> : <ChatBubbleOutline fontSize="inherit" />}
                            </IconButton>
                        </div>
                        <HwGroupElevationLink openings={filteredOpenings} />
                        <div style={{flex: 1}} />
                        {!locked && <div style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            minWidth: 300,
                            alignItems: "center"
                        }}>
                            <IconButton2 style={{height: 16, marginRight: 10}}>
                                <AddCircleOutlineIcon fontSize="inherit"/>
                            </IconButton2>
                            <OpeningLookup
                                backgroundColor="transparent"
                                ignore={value.openings.map(o => o.id)}
                                onSelect={o => {
                                    if (value.openings.find(v => v.id === o.id)) return;

                                    edit(Object.assign({}, value, {
                                        openings: value.openings.concat([toHwOpening(o)]),
                                    }));
                                }}
                                onMultiSelect={o => {
                                    o = o.filter(oo => !value.openings.find(v => v.id === oo.id));
                                    o = distinctByKey<QuoteOpening>(o as QuoteOpening[], o => o.id.toString());

                                    edit(Object.assign({}, value, {
                                        openings: value.openings.concat(o.map(toHwOpening)),
                                    }));
                                }}
                                hwGroup={value.id}
                            />
                        </div>}
                        <div style={{display: "flex"}}>
                            {showRenameDialog &&
                                <RenameHardwareGroup value={value} onClose={() => setShowRenameDialog(false)}/>}
                            {showHardwarePreps && <PrepManager
                                value={props.value as any}
                                onCancel={() => setShowHardwarePreps(false)}
                                onDone={async (config) => {

                                    update.callback({
                                        value: Object.assign({}, props.value, {
                                            prepConfigJson: config,
                                            prepConfigIsCustom: hasCustomPrep(config),
                                        }),
                                    })

                                    setShowHardwarePreps(false)
                                }}/>}
                            <Grid container spacing={1} alignItems="center">
                                {isEditing ? <>
                                        {!showShortcodeDetail && update.LoadingElement &&
                                            <Grid item>{update.LoadingElement}</Grid>}
                                        <Grid item>
                                            <Button variant="outlined" color="primary" size="small"
                                                    onClick={() => setShowRenameDialog(true)}>
                                                Rename
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button variant="outlined" color="primary" size="small" onClick={() => {
                                                setValue(props.value);
                                                editCtx.onDone();
                                            }}>
                                                Cancel
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button variant="contained" color="primary" size="small" onClick={() => {
                                                update.callback({value: value})
                                            }}>
                                                Save Changes
                                            </Button>
                                        </Grid>
                                    </> :
                                    <>
                                        <Grid item>
                                            <PopoutMenu size="small" disabled={locked}>
                                                {onClose => <>
                                                    <List>
                                                        <MenuItem onClick={() => {
                                                            setShowRenameDialog(true)
                                                            onClose();
                                                        }}>Rename</MenuItem>
                                                        <MenuItem onClick={() => {
                                                            update.callback({
                                                                value: Object.assign({}, value, {
                                                                    id: 0,
                                                                    openings: [],
                                                                    name: "",
                                                                })
                                                            })
                                                            onClose();
                                                        }}>Duplicate</MenuItem>
                                                        {!allDrwHardware && (
                                                            <MenuItem onClick={() => {
                                                                setShowHardwarePreps(true);
                                                                onClose();
                                                            }}>Customize Hardware Preps</MenuItem>
                                                        )}
                                                        <MenuItem onClick={() => {
                                                            setShowBulkUpdateOpenings(true);
                                                            onClose();
                                                        }}>Openings (Bulk Edit)</MenuItem>
                                                        <MenuItem onClick={() => {
                                                            setShowBulkUpdateSuppliedBy(true);
                                                            onClose();
                                                        }}>Supplied By (Bulk Edit)</MenuItem>
                                                        {!allDrwHardware && (
                                                            <MenuItem onClick={() => {
                                                                onClose();
                                                                multiHinge.prompt(value).then(v => {
                                                                    update.callback({
                                                                        value: v,
                                                                    });
                                                                });
                                                            }}>Multi-Hinge Configuration</MenuItem>
                                                        )}
                                                        <MenuItem onClick={() => {
                                                            setShowTimeline(true);
                                                            onClose();
                                                        }}>View Timeline</MenuItem>
                                                        {!isQuoteMode && <MenuItem disabled={recalculate.loading || value.openings.length === 0} onClick={() => {
                                                            onClose();
                                                            recalculate.callback({openings: value.openings.map(o => o.id)});
                                                        }}>Recalculate</MenuItem>}
                                                        <Divider/>
                                                        <MenuItem style={{color: red["800"]}} onClick={async () => {
                                                            onClose();
                                                            const ok = await confirm.confirm("Are you sure you want to remove this heading?")
                                                            if (!ok) return;

                                                            update.callback({
                                                                value: Object.assign({}, value, {
                                                                    archived: true,
                                                                })
                                                            })

                                                        }}>Remove</MenuItem>
                                                    </List>
                                                </>}
                                            </PopoutMenu>
                                        </Grid>
                                    </>}
                            </Grid>
                        </div>
                    </div>
                </td>
            </tr>

            {!collapseHeader && filteredOpenings.map(o =>
                <tr
                    key={"opening-" + o.id}
                    style={{
                        backgroundColor:
                            ((changeset.proposing || previewChangeset.enabled) && !o.sessionChanged) || lockedQuoteMode ? cellInActive :
                                undefined,
                    }}
                >
                    <IconCell>
                        <IconButton2 disabled={locked} onClick={() => {
                            edit(Object.assign({}, value, {
                                openings: value.openings.filter(oo => oo.id !== o.id)
                            }));
                        }}>
                            <RemoveCircleOutline fontSize="inherit"/>
                        </IconButton2>
                    </IconCell>
                    <td colSpan={colCount - 2} style={{fontWeight: "500"}}>
                        <div style={{
                            display: "flex",
                            gap: 4,
                        }}>
                            <div>{o.descriptiveName} {o.doorName} -- {o.handing}{o.phase ? " -- " + phaseLabel(o.phase) : ""}</div>
                            <div style={{flex: 1}} />
                            <DegreesOpeningDetail value={o as any} hardwareGroup={value.id} />
                            {o.logictrl && <LogiCtrlOpeningCell
                                value={o.logictrl} opening={o.id} name={o.name}
                                onReload={() => {
                                  reloadRef.current();
                                }}
                            />}
                        </div>
                    </td>
                    <IconCell>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            width: "100%",
                        }}>
                            <a target="_blank" rel="noreferrer" className={noStyleLink}
                               href={`/project/${project}/shop-drawing/openings?${buildFilterLink({
                                   tableName: openingsTableName(project),
                                   filter: {
                                       type: "eq",
                                       search: {
                                           id: o.id
                                       },
                                   }
                               })}`}>
                                <IconButton2>
                                    <OpenInNewIcon fontSize="inherit"/>
                                </IconButton2>
                            </a>

                        </div>
                    </IconCell>
                </tr>)}
            {shouldCollapse &&
                <tr key={"collapsed"} style={{backgroundColor: changeset.proposing ? cellInActive : undefined}}>
                    <td></td>
                    <td colSpan={colCount - 1} style={{fontWeight: "500", paddingTop: 0, paddingBottom: 0}}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item>
                                {value.openings.length > 0 && value.openings[0].doorName}
                            </Grid>
                            <Grid item>
                                <i>({value.openings.length} openings)</i>
                            </Grid>
                            <Grid item>
                                <Button size="small"
                                        onClick={() => setCollapseHeader(!collapseHeader)}>{collapseHeader ? "Show" : "Hide"}</Button>
                            </Grid>
                        </Grid>
                    </td>
                </tr>}
            {info?.bothActive && <tr key={"group-header"}>
                <td colSpan={6}></td>
                <td style={{textAlign: "right"}}>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                        <InfoPopout value={"This group will use special \"Both Active\" product rule for preps"}/>
                        <div style={{width: 3}} />
                        {info?.activeHanding}
                    </div>
                </td>
                <td style={{textAlign: "right"}}>
                <div style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                        <div style={{fontSize: "0.8rem", height: "1em", overflow: "hidden", color: grey["800"], cursor: "pointer", marginRight: 3}}
                             onClick={e => {
                                 update.callback({
                                     value: Object.assign({}, value, {
                                        flipDoubleActiveHanding: !value.flipDoubleActiveHanding,
                                     }),
                                 });
                             }}>

                            <CompareArrowsIcon fontSize="inherit"/>
                        </div>

                        {info?.inActiveHanding}
                    </div>
                </td>
                <td></td>
                <td></td>
            </tr>}
            {hardwareFilter.filter(h => h.commonQty > 0 || h.activeQty > 0 || h.inactiveQty > 0).map((h, index) => {

                let defaultGrey = ((changeset.proposing || previewChangeset.enabled) && (!h.backup || h.backup?.archived === false)) || lockedQuoteMode
                const rowBg = defaultGrey ? cellInActive : undefined

                const checkBg = (key: keyof Omit<HardwareItem, "backup">) => {
                    if (!h.backup) return undefined;

                    const v = h as any as HardwareItem;
                    return defaultGrey && v.backup && v[key] !== v.backup?.[key] ? "white" : undefined
                }

                const updateHardwareItem = (key: keyof Omit<HardwareItem, "backup">, newValue: any) => {
                    edit(Object.assign({}, value, {
                        hardware: value.hardware.map((hh: EditHardwareItem) => {
                            if (hh.product === h.product) {
                                return Object.assign({}, hh, {
                                    [key]: newValue,
                                })
                            } else {
                                return hh;
                            }
                        }),
                    }))
                }

                const updateObj = (newValue: EditHardwareItem) => {
                    return edit(Object.assign({}, value, {
                        hardware: value.hardware.map((hh: EditHardwareItem) => {
                            if (hh.product === h.product) {
                                return newValue;
                            } else {
                                return hh;
                            }
                        }),
                    }))
                }

                return (<tr key={"hardware-" + index} style={{
                    backgroundColor: rowBg,
                    opacity: h.customerSupplied ? 0.5 : undefined,
                }}>
                    <IconCell>
                        {isEditing && <RowNumberIndicator value={index+1} />}
                        <IconButton2 disabled={locked} onClick={() => {
                            updateObj(Object.assign({}, h, {
                                activeQty: 0,
                                inactiveQty: 0,
                                commonQty: 0,
                            }))
                        }}>
                            <RemoveCircleOutline fontSize="inherit"/>
                        </IconButton2>
                    </IconCell>
                    <td></td>
                    <td>
                        <div style={{display: "flex", alignItems: "center", paddingBottom: 2}}>
                            <span style={{borderBottom: "1px solid", cursor: "pointer", marginRight: 8}}
                                  onClick={() => productEdit.show(h.product)}>
                                {h.name}{h.isCustom ? " (Custom)" : null}
                            </span>
                        </div>
                    </td>
                    <td>

                        {h.discontinued ?
                            <div>
                                <div style={{
                                    textDecoration: "line-through"
                                }}>{getProductCode(h)}</div>
                                <div style={{marginTop: 4}}>
                                    <Chip color="warning" size="small" icon={<Warning />} label="Discontinued" />
                                </div>
                            </div>: <>
                                {getProductCode(h)}
                        </>}
                    </td>
                    <td>{h.finish}</td>
                    <td>
                        <ProductDimensioned id={h.product} show={!!h.dimensionFormula} dimensions={h.calcByDimension}/>
                    </td>
                    {h.productIsAutoQtyHinge ? <>
                        <AutoForHingeCell value={h} group={props.value} part="active" onOverride={value => {
                            updateObj(Object.assign({}, h, {
                                overrideHingeActiveQty: value,
                            }))

                            return Promise.resolve();
                        }}/>
                        <AutoForHingeCell value={h} group={props.value} part="inactive" onOverride={value => {
                            updateObj(Object.assign({}, h, {
                                overrideHingeInActiveQty: value,
                            }))

                            return Promise.resolve();
                        }}/>
                        <td style={{textAlign: "right"}}>-</td>
                    </> : <>
                        <EditCell disabled={locked}
                                  inputStyle={{width: qtyWidth}} value={h.activeQty.toString()}
                                  style={{
                                      textAlign: "right",
                                      backgroundColor: checkBg("activeQty")
                                  }}
                                  onChange={v => {
                                      updateHardwareItem("activeQty", parseInt(v))
                                  }}/>
                        <EditCell disabled={locked} inputStyle={{width: qtyWidth}} value={h.inactiveQty.toString()}
                                  style={{
                                      textAlign: "right",
                                      backgroundColor: checkBg("inactiveQty")
                                  }} onChange={v => {
                            updateHardwareItem("inactiveQty", parseInt(v))
                        }}/>
                        <EditCell disabled={locked} inputStyle={{width: qtyWidth}} value={h.commonQty.toString()}
                                  style={{
                                      textAlign: "right",
                                      backgroundColor: checkBg("commonQty"),
                                  }}
                                  onChange={v => {
                                      updateHardwareItem("commonQty", parseInt(v))
                                  }}/>
                    </>
                    }
                    <StatusCell value={h}/>
                    <EditCell disabled={locked} value={h.note} style={{
                        backgroundColor: checkBg("note"),
                    }} onChange={v => {
                        updateHardwareItem("note", v);
                    }} placeholder=""/>
                    <IconCell>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            width: "100%",
                        }}>
                            <ConfigureHardwareItemExt value={h} openings={props.value.openings as HwOpening[]} />
                            <ItemCalcErrors value={h.calcByDimension} />
                            <SupplierChip value={h} alwaysShow={isEditing} />
                            <HardwareItemMenu disabled={locked} value={h} onChange={updateObj}/>
                        </div>
                    </IconCell>
                </tr>)
            })}
            {locked ? null : <tr key={
                /* re-render for each change to trigger auto-focus product lookup */
                "add-row-" + lastEditedTimestamp.toString()
            } style={{
                backgroundColor: changeset.proposing ? cellInActive : undefined,
            }}>
                <td/>
                <td colSpan={colCount - 1} style={{fontWeight: "500", paddingLeft: indentWidth - 2}}>
                    <ProductLookup
                        disabled={locked}
                        backgroundColor="transparent"
                        placeholder="Enter Shortcode or start typing product name"
                        kind="hardware"
                        includeShortCodesForProject={project}
                        autoFocus={(isEditing && lastActionWasSearch) || (props.isCreate && (lastActionWasSearch || lastActionWasSearch === undefined))}
                        renderProductForOpening={first(props.value.openings, o => !isQuoteMode)?.id}
                        renderProductForQuoteOpening={first(props.value.openings, o => isQuoteMode)?.id}
                        onSelect={async (selected: ProductSearchResult | ShortCode) => {

                            const hwOpeningType = props.value.openings.map(o => getHwOpeningType(o.openingType, o.frameProfile === "COM")).find(v => !!v);
                            console.log("onSelect2", selected);

                            if (isShortCode(selected)) {
                                edit(Object.assign({}, value, {
                                    hardware: toProductQty(selected, hwOpeningType)
                                }), "search");
                            } else {
                                const update = value.hardware.slice(0)
                                const result = await api.products.getById(selected.id)
                                const toAdd = toProductQty(result, hwOpeningType);
                                toAdd.map(a => {
                                    const existing = update.find(u => u.product === a.product);
                                    if (existing) {
                                        existing.activeQty += a.activeQty;
                                        existing.inactiveQty += a.inactiveQty;
                                        existing.commonQty += a.commonQty;
                                        return null;
                                    }

                                    update.push(a)
                                    return null;
                                })

                                edit(Object.assign({}, value, {
                                    hardware: update,
                                }), "search");
                            }

                        }}
                    />
                </td>
            </tr>}
        </>
    )
});

export function getProductCode(input: HardwareItem | EditHardwareItem) {
    if(input.calcByDimension && input.calcByDimension.length > 0) {
        return Array.from(new Set(input.calcByDimension.map(c => c.productCode))).join(", ");
    }

    return input.productCode;
}

export const noStyleLink = css({
    color: "inherit",
    textDecoration: "none",
})

export function toHwOpening(input: QuoteOpening | Opening): HwOpening {
    return {
        seqNumber: input.seqNumber,
        phase: input.phase,
        qty: input.qty,
        name: input.name,
        descriptiveName: input.name,
        doorName: input.hwDescriptiveName || input.name || "",
        handing: input.handing,
        id: input.id,
        sessionChanged: true,
        nominalHeight: input.nominalHeight,
        nominalWidth: input.nominalWidth,
        inactiveDoorWidth: input.inactiveDoorWidth,
        screenElevation: input.screenElevation,
        openingType: input.openingType,
        frameProfile: input.frameProfile,
        degreesOfOpeningActive: input.degreesOfOpeningActive,
        degreesOfOpeningInactive: input.degreesOfOpeningInactive,
        drwHardwareElevation: input.drwHardwareElevation,
        drwHardwareElevationName: input.drwHardwareElevationName,
        // @ts-ignore
        logictrl: input.logictrl || undefined,
    }
}